import { useState } from 'react'
import { AlertTriangle, ClipboardCheck, Lock, Pencil } from 'lucide-react'
import { formatDateTime } from '../shared/IssueRow'
import type {
  SystemIssueMessage,
  SystemIssueResolutionSummary,
} from '../../types/system-issues'

/**
 * Resumo da solução na conversa do chamado — só para desenvolvedores: o
 * painel para escrever (abre ao resolver), a faixa que lembra quando a
 * resolução atual ficou sem resumo e o cartão "Solução" na linha do tempo.
 */

/** Painel "O que foi feito para resolver?", logo acima do campo de mensagem. */
export function ResolutionSummaryEditor({
  initial,
  saving,
  compact,
  onSave,
}: {
  initial: string
  saving: boolean
  compact: boolean
  onSave: (summary: string) => void
}) {
  const [text, setText] = useState(initial)
  const canSave = text.trim().length > 0 && !saving
  const save = () => {
    if (canSave) onSave(text.trim())
  }

  return (
    <section
      aria-label="Resumo da solução"
      className={`border-t border-teal-200 bg-teal-50/60 pb-4 pt-3 dark:border-teal-900/60 dark:bg-teal-900/15 ${
        compact ? 'px-4' : 'px-8'
      }`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <label htmlFor="resolution-summary" className="text-sm font-semibold text-body">
          O que foi feito para resolver?
        </label>
        <span className="inline-flex items-center gap-1 text-[11px] text-muted">
          <Lock className="h-3 w-3" aria-hidden />
          Só os desenvolvedores veem este resumo.
        </span>
      </div>
      <textarea
        id="resolution-summary"
        autoFocus
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Ctrl/⌘+Enter salva, como em outros campos de texto do sistema.
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            save()
          }
        }}
        placeholder="Descreva brevemente o que foi corrigido ou feito para resolver este chamado."
        className="mt-2 w-full resize-y rounded-lg border border-border-subtle bg-surface px-3 py-2 text-[15px] text-body placeholder:text-subtle focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/40"
      />
      {/* Só "Salvar": quem sai sem escrever encontra a faixa "Falta descrever
          a solução" ao voltar ao chamado. */}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          className="h-9 rounded-lg bg-teal-700 px-4 text-sm font-semibold text-white transition hover:bg-teal-800 disabled:opacity-50"
        >
          {saving ? 'Salvando…' : 'Salvar resumo'}
        </button>
      </div>
    </section>
  )
}

/** Resolução atual sem resumo (saiu sem salvar, resolvido por outro dev). */
export function MissingSummaryBanner({
  compact,
  onWrite,
}: {
  compact: boolean
  onWrite: () => void
}) {
  return (
    <div
      role="status"
      className={`flex flex-wrap items-center justify-between gap-2 border-t border-amber-200 bg-amber-50 py-2.5 text-[13px] text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-300 ${
        compact ? 'px-4' : 'px-8'
      }`}
    >
      <span className="inline-flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        Falta descrever a solução deste chamado.
      </span>
      <button
        type="button"
        onClick={onWrite}
        className="inline-flex h-8 items-center gap-1.5 rounded-md bg-amber-600 px-3 text-[13px] font-semibold text-white transition hover:bg-amber-700"
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden />
        Descrever solução
      </button>
    </div>
  )
}

/** Cartão "Solução" na linha do tempo, logo após o "Chamado resolvido". */
export function ResolutionSummaryCard({
  summary,
  onEdit,
}: {
  summary: SystemIssueResolutionSummary
  /** Só no resumo da resolução atual (o backend só grava esse). */
  onEdit?: () => void
}) {
  return (
    <article
      aria-label="Solução"
      className="ml-[54px] max-w-[min(57.5rem,80%)] rounded-xl border border-teal-200 bg-teal-50/60 px-5 pb-3.5 pt-3 dark:border-teal-900/60 dark:bg-teal-900/15"
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        <ClipboardCheck className="h-4 w-4 text-teal-700 dark:text-teal-400" aria-hidden />
        <span className="font-semibold text-teal-800 dark:text-teal-300">Solução</span>
        <span className="text-muted">
          {summary.author ? `${summary.author.full_name} · ` : ''}
          {formatDateTime(summary.updated_at)}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-subtle">
          <Lock className="h-3 w-3" aria-hidden />
          só devs
        </span>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[13px] font-medium text-teal-800 transition hover:bg-teal-100 dark:text-teal-300 dark:hover:bg-teal-900/40"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            Editar
          </button>
        )}
      </header>
      <p className="mt-1.5 whitespace-pre-wrap text-[15px] text-body">{summary.summary}</p>
    </article>
  )
}

/** Evento "Chamado resolvido" (`status_change` para `resolved`). */
function isResolvedEvent(m: SystemIssueMessage): boolean {
  const p = m.system_payload as { status_change?: { to?: string } } | null
  return m.kind === 'system' && p?.status_change?.to === 'resolved'
}

/**
 * Onde cada resumo entra na conversa: depois do evento "Chamado resolvido"
 * da resolução dele — o mais próximo do `resolved_at` (o relógio do banco e
 * o do servidor podem diferir alguns ms). Sem evento, vai para o fim.
 */
export function anchorSummaries(
  messages: SystemIssueMessage[],
  summaries: SystemIssueResolutionSummary[],
): { after: Map<number, SystemIssueResolutionSummary[]>; trailing: SystemIssueResolutionSummary[] } {
  const events = messages.filter(isResolvedEvent)
  const after = new Map<number, SystemIssueResolutionSummary[]>()
  const trailing: SystemIssueResolutionSummary[] = []
  for (const s of summaries) {
    const at = Date.parse(s.resolved_at)
    let best: SystemIssueMessage | null = null
    for (const e of events) {
      if (!best || Math.abs(Date.parse(e.created_at) - at) < Math.abs(Date.parse(best.created_at) - at)) {
        best = e
      }
    }
    if (best) after.set(best.id, [...(after.get(best.id) ?? []), s])
    else trailing.push(s)
  }
  return { after, trailing }
}
